import { memo, useEffect, useRef, useState } from "react";
import { tagBareFences } from "@/services/multi-agent/codePreview";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Circle,
  Copy,
  Download,
  FileCode2,
  FileText,
  GitCompare,
  HelpCircle,
  Loader2,
  MinusCircle,
  ShieldCheck,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { MessageFeedback } from "@/components/molecules/multi-agent/MessageFeedback";
import { useCopyToClipboard } from "@/hooks/multi-agent/useCopyToClipboard";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { exportArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import { presentError } from "@/services/multi-agent/errorRouting";
import {
  activityBreakdown,
  activityGroups,
  activityHeadline,
  proseOf,
  referenceCard,
  toolLine,
  type ChangePreview,
  type DerivedReference,
  type PlanSummary,
  type PlanSummaryComponent,
  type PlanSummaryRow,
  type OpenableSection,
  type ReferenceTarget,
  type RunOutput,
} from "@/services/multi-agent/transcript";
import { HighlightedLines, highlightLanguage, useIsDarkPainted, useShikiTokens } from "./codeHighlight";
import type { Error as ProtocolError, Evidence, Part, StepStatus } from "@/services/multi-agent/protocol";
import { CollapsibleMarkdownTable } from "@/components/organisms/studio/CollapsibleMarkdownTable";
import { buildChatCodeRenderer } from "./ChatCodeBlock";
import type { SourceChip } from "@/services/multi-agent/transcript";
import type { CodePayload } from "@/stores/weaveWorkspaceStore";

/**
 * The five kinds of block an assistant turn is made of.
 *
 * ONE VISUAL WEIGHT PER LEVEL, and that is the whole design. Prose is the only thing at full
 * reading weight; activity and reasoning are quiet single lines with a disclosure; a reference is a
 * compact card whose job is to OPEN something rather than to contain it. Before this, every
 * protocol surface rendered at the same weight in one column, so nothing was primary.
 *
 * Every block is `memo`'d on its parts. `mapMessages` in the reducer preserves the identity of
 * untouched messages, so a `message.part_delta` re-renders only the streaming turn — and within it,
 * only the block whose parts changed.
 */

// ── Fenced blocks inside prose ──────────────────────────────────────────────

/**
 * The `pre` override that turns a fenced block into a preview with an opener.
 *
 * `pre` AND NOT `code`, deliberately. Streamdown hands `code` every inline span too, so overriding
 * it would put a preview card around `` `Opportunity` `` in the middle of a sentence. `pre` is the
 * fence and only the fence.
 *
 * Merged through `SkillResultMarkdown`'s existing `components` prop — the same extension point
 * `ResultColumn` uses for its artifact interceptor, and the caller's spread wins over the
 * defaults. No second markdown pipeline (§14).
 */
export function buildCodeInterceptor(onOpen: (payload: CodePayload) => void, isStreaming = false) {
  return {
    /*
     * ── THE `pre` OVERRIDE IS GONE, AND THAT IS THE WHOLE FIX ────────────────
     *
     * This used to be `pre: … <CodePreviewBlock/>`, on the assumption that a `components` entry
     * layers on top of the renderer stack. It does not: `pre` replaces the path streamdown's
     * fenced-block pipeline lives on. Measured in a browser on one answer carrying an Apex class,
     * a JSON result, a mermaid graph and a shell script — with the override, 0 diagrams, 0 copy
     * buttons and no highlighting; without it, a diagram, five copy buttons and four Monacos.
     *
     * So the code path is swapped where the renderers actually register, and everything else in the
     * pipeline is left alone. See `ChatCodeBlock` for what the chat renders instead of a Monaco.
     */
    codeRenderers: [buildChatCodeRenderer({ isStreaming, onOpen })],
    /*
     * Registered through the caller's `components`, not in `SkillResultMarkdown`'s shared defaults,
     * because a collapsed table is right for a CONVERSATION and wrong for a report: the playbook and
     * agent run views exist to show the table. `SkillResultMarkdown` declares its own overrides
     * before the caller's spread precisely so a surface can take one back, which is what this does.
     */
    components: { table: CollapsibleMarkdownTable },
  };
}

/*
 * `WiredCodePreview` and `readFence` USED TO LIVE HERE, and are deleted rather than parked.
 *
 * Both existed to serve the `components.pre` override: one read the language and text back out of
 * another library's element tree, the other subscribed to the workspace store so an already-painted
 * block could show which snippet was open. With the fence rendered by a `renderers` entry, the
 * language and the code arrive as arguments and there is no element tree to parse — and the block
 * no longer tracks a workspace section, because `Open` now hands it to the editor's tab strip,
 * which shows what is open in its own tabs. §13: when a change makes a component obsolete, delete
 * it. `CodePreviewBlock` went with them.
 */


/*
 * ── WHY THESE BLOCKS COMPARE THEIR CONTENT, NOT THEIR PROPS ──────────────────
 *
 * THE FLICKER THIS FIXES, measured in the browser: 20 text deltas on ONE part produced 98 added
 * elements and tore `foreignObject` nodes out of a mermaid diagram that had not changed — the
 * diagram visibly re-drew while unrelated text streamed.
 *
 * `groupTurnParts` rebuilds its block list on every render, so every block gets a BRAND NEW `parts`
 * array each frame. `memo`'s default comparison is shallow and identity-based, so it never matched:
 * every prose block in the answer re-rendered on every SSE frame, `SkillResultMarkdown` re-rendered
 * with it, streamdown re-parsed the markdown, and shiki re-highlighted every fence while mermaid
 * re-laid-out every diagram — for blocks whose text was byte-identical to the frame before.
 *
 * The fix is to compare what the block actually RENDERS. `proseOf` is the same accumulation the
 * component itself uses (a string concat over a handful of parts — far cheaper than the render it
 * prevents), so a block re-renders exactly when its own text changes and not once more. The
 * streaming tail still updates every frame, which is the whole point; the settled content above it
 * now holds still.
 *
 * NOT solved by memoising the array in `WeaveTranscript`: the array's CONTENTS are new objects too,
 * because the reducer replaces the streaming part on each delta. Content equality is the only
 * comparison that answers "did this block change".
 */
function samePartsText(a: readonly Part[], b: readonly Part[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    // `state` matters as well as text: a part going `streaming` -> `available` ends the cursor.
    if (a[i].part_id !== b[i].part_id || a[i].state !== b[i].state) return false;
  }
  return proseOf(a) === proseOf(b);
}

export const ProseBlock = memo(function ProseBlock({
  parts,
  streaming,
  codeInterceptor,
}: {
  parts: Part[];
  streaming: boolean;
  /** From `buildCodeInterceptor`. Omitted, fenced blocks render as ordinary markdown. */
  /** From `buildCodeInterceptor`: the chat's fence renderer plus its table override. */
  codeInterceptor?: { codeRenderers?: readonly unknown[]; components?: Record<string, unknown> };
}) {
  /*
   * One markdown render for the whole run of text parts, not one per part.
   *
   * A sentence split across two `text` parts is one sentence; rendering them separately would put a
   * paragraph break inside it. `proseOf` concatenates in `index` order, which is why it exists.
   */
  /*
   * `tagBareFences` runs on the way IN, because the language is the only thing streamdown dispatches
   * a custom renderer on — an untagged fence can never reach `ChatCodeBlock` otherwise, and lands in
   * the library's own block with no bounded height, no `Open`, and a copy/download pair its parent's
   * paint containment clips. See the function's note; nothing but the fence's tag is changed.
   */
  const content = tagBareFences(proseOf(parts));
  if (!content.trim()) return null;
  /*
   * THE LIVE EDGE. Without a cursor a paused stream and a finished answer look identical, so a
   * reader cannot tell whether to wait or to start typing. Wrapped rather than passed into
   * `SkillResultMarkdown` because the cursor belongs after the rendered markdown, and the renderer
   * owns everything inside it.
   */
  if (streaming) {
    return (
      <div>
        <SkillResultMarkdown
          content={content}
          isStreaming
          className="ma-answer"
          codeRenderers={codeInterceptor?.codeRenderers}
          components={codeInterceptor?.components}
        />
        <StreamCursor />
      </div>
    );
  }
  return (
    <SkillResultMarkdown
      content={content}
      // `isStreaming` lets the renderer hold off on expensive passes (mermaid) until the text
      // settles — the same signal `ChatPanel` gives it on the legacy path.
      isStreaming={streaming}
      // `ma-answer`, NOT `ma-prose`: the latter is the whole answer area and carries
      // `padding: 32px 24px 56px`, which put ~88px between every block of a single turn.
      className="ma-answer"
      codeRenderers={codeInterceptor?.codeRenderers}
      components={codeInterceptor?.components}
    />
  );
}, (prev, next) =>
  prev.streaming === next.streaming &&
  prev.codeInterceptor === next.codeInterceptor &&
  samePartsText(prev.parts, next.parts));

// ── Reasoning ───────────────────────────────────────────────────────────────

export const ReasoningBlock = memo(function ReasoningBlock({
  parts,
  streaming = false,
}: {
  parts: Part[];
  /**
   * Is the TURN this reasoning belongs to still live? Only the last turn's block ever receives
   * `true` — every past turn is by definition settled, so it always renders collapsed as
   * "Reasoning". See `WeaveTranscript` for where this is computed from `turnPhase`.
   */
  streaming?: boolean;
}) {
  const content = proseOf(parts);

  /*
   * ── OPEN WHILE LIVE, CLOSED ONCE SETTLED — REQUESTED DIRECTLY, VERIFIED AGAINST WHAT DATA EXISTS ─
   *
   * This used to be collapsed unconditionally: correct for a reasoning block a reader did not ask
   * to see, wrong for one that is HAPPENING RIGHT NOW — a live thought process reads as more alive
   * open than hidden behind a click the reader has to make while it is still forming.
   *
   * `open` is local state, not a direct `open={streaming}`, because a native `<details>` needs to
   * stay toggleable by the reader in between — forcing it from a prop on every render would refuse
   * a manual close while still streaming and a manual re-open once settled. The effect below moves
   * it only AT the two edges (streaming starts / streaming ends), via a ref tracking the previous
   * value, so it does not refight the reader's own click on every one of the many re-renders a
   * live block goes through while `streaming` itself stays constant.
   */
  const [open, setOpen] = useState(streaming);
  const wasStreaming = useRef(streaming);
  /*
   * "Reasoning · 8s" once settled — timed HERE, because the protocol carries no timestamps on a
   * part. Measured from the first render that saw this block live to the one that saw it settle;
   * a block that was never live in this tab (a reload, a past turn) has no figure and says
   * "Reasoning", which is the honest word for a number nobody measured.
   */
  const liveSince = useRef<number | null>(streaming ? Date.now() : null);
  const [thoughtFor, setThoughtFor] = useState<number | null>(null);
  useEffect(() => {
    if (wasStreaming.current !== streaming) {
      setOpen(streaming);
      wasStreaming.current = streaming;
      if (streaming) {
        liveSince.current = Date.now();
      } else if (liveSince.current !== null) {
        setThoughtFor(Math.max(1, Math.round((Date.now() - liveSince.current) / 1000)));
        liveSince.current = null;
      }
    }
  }, [streaming]);

  if (!content.trim()) return null;

  /*
   * THE LIVE TAIL, for a reader who closed the disclosure while it was still thinking: the last
   * line of what is being written, beside the label, so the block keeps moving without being open.
   * Nothing is summarised or invented — it is the newest text, cut to one line.
   */
  const tail = streaming && !open ? content.trim().split(/\n+/).filter(Boolean).at(-1) ?? "" : "";

  /*
   * ── THE STEPPER IS PARAGRAPH BOUNDARIES, NOT INVENTED STRUCTURE ─────────────────────────────────
   *
   * `reasoning` parts are free text (§2.5) — there is no `steps[]` the contract gives this block, so
   * "a step" here is a paragraph break the model itself chose, the same boundary any reader already
   * uses to see where one thought ends and the next begins. Splitting on it is presentation over
   * data that is already there, not fabricated data.
   */
  const steps = content
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <details
      className="group"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronRight
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90 motion-reduce:transition-none"
        />
        {/*
          THE LABEL IS THE ANSWER TO "is this still going". "Thinking" while the turn is live — set
          in the route's shimmer text, the same sweep the warm-up caption uses, so the word itself
          moves — and "Reasoning" (with "· Ns" when this tab timed it) the moment it settles.
          A reader glancing at the collapsed summary can tell which one it is without opening it.
        */}
        {streaming ? (
          <span className="ma-weave-shimmer shrink-0 font-medium">Thinking</span>
        ) : (
          <span className="shrink-0">
            Reasoning
            {thoughtFor !== null && <span className="text-muted-foreground"> · {thoughtFor}s</span>}
          </span>
        )}
        {tail && (
          <span aria-hidden className="min-w-0 max-w-[48ch] truncate text-muted-foreground">
            {tail}
          </span>
        )}
      </summary>
      {/* Smaller than body, on request — reasoning is chain-of-thought, not the answer, and reads
          more like a caption/footnote at this size than at body weight. */}
      <div className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
        {steps.length > 1 ? (
          /*
            THE STEPPER: attractive ("very small… not colorful"), one deliberate colour ("still
            greyish… blue"), given room (referenced against this CLI's own tool timeline), and now
            PER-STEP STATUS, on a precise correction to the block-level version this replaces:
            "when I get streaming text, the dot should be blue — as soon as THAT text finishes, turn
            green — then the SECOND one starts and does the same." That is a per-paragraph state the
            protocol does not carry (there is no per-paragraph streaming boundary, only the whole
            part's), but it is fully DERIVABLE without inventing anything: text only ever grows at
            the tail, so a paragraph that already has another paragraph AFTER it is, by definition,
            finished — only the LAST paragraph can still be the one currently being written. So: the
            last step is blue exactly while the block itself is still streaming, and every step
            before it — plus the last one too, the instant the block settles — is green. The rail
            stays green throughout: it is the path already walked, not the live edge.
          */
          <ol className="relative ml-1 pl-7" style={{ "--ma-step-color": "var(--ma-state-ok)" } as React.CSSProperties}>
            {/* A highlight travels down the rail while the thought is still forming. */}
            <span
              aria-hidden
              className={cn(
                "ma-stepper-rail absolute left-2 top-1 bottom-1 w-[3px] rounded-full",
                streaming && "ma-stepper-rail--live",
              )}
            />
            {steps.map((step, i) => {
              const isLast = i === steps.length - 1;
              const live = streaming && isLast;
              return (
                <li key={i} className={cn("relative pb-5 last:pb-0", isLast && streaming && "ma-fade-up")}>
                  <span
                    aria-hidden
                    className={cn(
                      "ma-stepper-dot absolute -left-[26px] top-0.5 h-4 w-4 rounded-full border-2 border-background shadow-sm",
                      live && "animate-pulse",
                    )}
                    style={{ "--ma-step-color": live ? "var(--ma-accent)" : "var(--ma-state-ok)" } as React.CSSProperties}
                  />
                  {/*
                    `isStreaming` on the LIVE paragraph only: the markdown renderer animates new
                    tokens in (the same treatment the answer gets) and keeps an unclosed fence or
                    list from flashing; settled paragraphs render plainly. The cursor marks the edge.
                  */}
                  <SkillResultMarkdown content={step} isStreaming={live} className="ma-answer ma-answer-quiet" />
                  {live && <StreamCursor />}
                </li>
              );
            })}
          </ol>
        ) : (
          /*
           * THE SINGLE-PARAGRAPH CASE — no stepper, because there is only one step, but it was
           * still the plain grey `border-border` the multi-step rail replaced. A single reasoning
           * blob is the COMMON case (most reasoning has no blank-line break in it), so this quiet
           * grey line was most of what a reader actually saw and is very likely what "the stepper
           * dot is still greyish" was reporting. Same status colour as the multi-step rail — blue
           * while live, green once settled — and a plain `var()`, not `color-mix()`: see the CSS
           * rule's own comment for why that distinction is load-bearing here.
           */
          <div
            className={cn("ma-stepper-rail-solo border-l-2 pl-3", streaming && "ma-stepper-rail-solo--live")}
            style={{ "--ma-step-color": streaming ? "var(--ma-accent)" : "var(--ma-state-ok)" } as React.CSSProperties}
          >
            <SkillResultMarkdown content={content} isStreaming={streaming} className="ma-answer ma-answer-quiet" />
            {streaming && <StreamCursor />}
          </div>
        )}
      </div>
    </details>
  );
}, (prev, next) => samePartsText(prev.parts, next.parts) && prev.streaming === next.streaming);

// ── Activity ────────────────────────────────────────────────────────────────

const TOOL_STATE_STYLE = {
  running: "text-muted-foreground",
  ok: "text-muted-foreground",
  failed: "text-[var(--ma-state-err-text)]",
} as const;

/** The word for each check's status — never colour alone (§16). */
const CHECK_WORD = { pending: "still running", ready: "ready", failed: "failed", skipped: "skipped" } as const;
const CHECK_ICON = { pending: Loader2, ready: Check, failed: AlertTriangle, skipped: MinusCircle } as const;
const CHECK_STYLE = {
  pending: "text-muted-foreground",
  ready: "text-[var(--ma-state-ok-text)]",
  failed: "text-[var(--ma-state-err-text)]",
  skipped: "text-muted-foreground",
} as const;

export interface ActivityBlockProps {
  parts: Part[];
  /** The run's evidence checks, joined to this group. See `TurnView.checks`. */
  checks?: readonly Evidence[];
  /** Opens the workspace's Activity section on a call, or the Evidence section on a check. */
  onOpen?: (section: OpenableSection, focusRef: string | null) => void;
}

/**
 * THE EVIDENCE ACCORDION — what the run looked at and what it verified, in one closed panel.
 *
 * It replaced a one-line "Used 3 tools" disclosure. That line was honest but it hid the one thing a
 * reader deciding whether to approve wants at a glance: the SHAPE of the work — how much metadata
 * was read, what was searched, which checks ran — which is exactly what the reference design's
 * evidence panel puts in its header ("9 source files · 4 searches · 1 metadata audit"). So the
 * closed state carries the counts by the backend's own `category`, and the open state groups the
 * calls under those categories as chips, with each chip's `summary` in quiet ink beside its name.
 * Level 3 — arguments, ids, hop numbers — is still not here; a chip opens the Activity section,
 * where there is room for it.
 *
 * CHECKS FIRST. `RunState.evidence` items (impact, tests, scope…) are rows above the groups, each
 * with its kind as an eyebrow and the backend's own summary — the "TESTS · 0 classes covered" rows
 * of the reference. They are the verdicts; the groups below are the work that produced them.
 */
export const ActivityBlock = memo(function ActivityBlock({ parts, checks = [], onOpen }: ActivityBlockProps) {
  const groups = activityGroups(parts);
  const lines = parts.map(toolLine);
  const running = lines.some((l) => l.state === "running");
  const failed = lines.some((l) => l.state === "failed") || checks.some((c) => c.status === "failed");
  const headline = parts.length ? activityHeadline(parts) : null;
  const checkCount = checks.length ? `${checks.length} check${checks.length === 1 ? "" : "s"}` : null;
  const breakdown = activityBreakdown(groups);

  return (
    <details
      className="group rounded-lg border border-border bg-[var(--ma-surface-soft)]"
      open={false}
      data-weave-evidence=""
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-3 py-2 text-[13px]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          failed ? "text-[var(--ma-state-err-text)]" : "text-foreground",
        )}
      >
        {running ? (
          <Loader2 aria-hidden className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none" />
        ) : (
          <ChevronRight
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90 motion-reduce:transition-none"
          />
        )}
        <ShieldCheck aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-medium">Evidence</span>
        {(headline || checkCount) && (
          <span className={failed ? undefined : "text-muted-foreground"}>
            {[headline, checkCount].filter(Boolean).join(" · ")}
          </span>
        )}
        {breakdown && <span className="text-[12px] text-muted-foreground">{breakdown}</span>}
      </summary>

      <div className="border-t border-border/70">
        {checks.length > 0 && (
          <ul className="flex flex-col gap-1 px-3 py-2">
            {checks.map((check) => {
              const Icon = CHECK_ICON[check.status] ?? Loader2;
              return (
                <li key={check.evidence_id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <Icon
                    aria-hidden
                    className={cn(
                      "h-3 w-3 shrink-0 self-center",
                      CHECK_STYLE[check.status],
                      check.status === "pending" && "animate-spin motion-reduce:animate-none",
                    )}
                  />
                  {/*
                    ── THE LABEL COLUMN HAS TO FIT THE LONGEST LABEL ────────────────────────────
                    REPORTED: "GROUNDEDNESS" painted straight through the summary beside it. This
                    was `w-20` — a fixed 80px with `shrink-0` and no truncation — and the contract's
                    longest `kind` values (`groundedness`, `dependencies`, `requirements`, all 12
                    characters) render wider than that at 11px uppercase with `tracking-wide`. A
                    flex item that cannot shrink and does not clip simply overflows its own box, so
                    the text ran under the summary, which starts at the 80px mark.

                    `w-28` fits every kind the protocol defines today. `truncate` is the part that
                    matters: it makes the overlap UNREPRESENTABLE for a longer kind added later,
                    rather than leaving the next one to rediscover this. `title` keeps the full word
                    reachable if it ever does clip.
                  */}
                  <span
                    title={check.kind}
                    className="w-28 shrink-0 truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {check.kind}
                  </span>
                  <span className="min-w-0 flex-1 basis-48 text-[13px] text-foreground">
                    {check.summary || CHECK_WORD[check.status]}
                  </span>
                  <span className="sr-only">{CHECK_WORD[check.status]}</span>
                  {check.blocking_for_approval && check.status !== "ready" && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      required
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {groups.map((group) => (
          <section
            key={group.category ?? "uncategorised"}
            aria-label={group.label}
            className="border-t border-border/60 px-3 py-2 first:border-t-0"
          >
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
              <span className="ml-1.5 font-medium normal-case tracking-normal">{group.parts.length}</span>
            </p>
            <ul className="flex flex-wrap gap-1.5">
              {group.parts.map((part) => {
                const line = toolLine(part);
                const Tag = onOpen ? "button" : "span";
                return (
                  <li key={part.part_id} className="min-w-0 max-w-full">
                    <Tag
                      {...(onOpen ? { type: "button", onClick: () => onOpen("activity", part.ref ?? null) } : {})}
                      title={line.summary ?? undefined}
                      className={cn(
                        "inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-left text-[12px]",
                        onOpen && "hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        line.state === "failed" && "border-ma-err-line",
                      )}
                    >
                      {line.state === "failed" ? (
                        <AlertTriangle aria-hidden className="h-3 w-3 shrink-0 text-[var(--ma-state-err-text)]" />
                      ) : line.state === "running" ? (
                        <Loader2 aria-hidden className="h-3 w-3 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none" />
                      ) : (
                        <FileText aria-hidden className="h-3 w-3 shrink-0 text-muted-foreground" />
                      )}
                      <span className={cn("truncate", TOOL_STATE_STYLE[line.state], line.state === "ok" && "text-foreground")}>
                        {line.label}
                      </span>
                      {line.summary && (
                        <span className="truncate text-muted-foreground">· {line.summary}</span>
                      )}
                      {/* `null` when the call has not finished — never a fabricated 0ms. */}
                      {line.duration && (
                        <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{line.duration}</span>
                      )}
                    </Tag>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </details>
  );
}, (prev, next) => {
  // Identity is the calls' ids and states plus the checks' ids, statuses and summaries.
  if (prev.parts.length !== next.parts.length || prev.onOpen !== next.onOpen) return false;
  if (!prev.parts.every((p, i) => p.part_id === next.parts[i].part_id && p.state === next.parts[i].state && p.ref === next.parts[i].ref)) return false;
  const a = prev.checks ?? [];
  const b = next.checks ?? [];
  if (a.length !== b.length) return false;
  return a.every((c, i) => c.evidence_id === b[i].evidence_id && c.status === b[i].status && c.summary === b[i].summary);
});

// ── Reference card ──────────────────────────────────────────────────────────

const REFERENCE_ICON: Record<ReferenceTarget, typeof FileText> = {
  plan: Sparkles,
  changeset: GitCompare,
  deploy: UploadCloud,
  evidence: ShieldCheck,
  artifact: FileText,
};

export interface ReferenceBlockProps {
  part: Part;
  /** A short live subtitle from `RunState` — "3 steps · config". Omitted when there is nothing true to say. */
  subtitle?: string | null;
  onOpen: (section: OpenableSection, focusRef: string | null) => void;
  active?: boolean;
  /**
   * For a `changeset` card: the produced code, previewed under it — `changePreviews`. A card is
   * a card whether a part or the state produced it, so this takes exactly what
   * `DerivedReference.previews` carries. Ignored on every other target.
   */
  previews?: ChangePreview[];
  previewsHidden?: number;
  /** The workspace's `focusRef` — which previewed file, if any, the open board is on. */
  activeRef?: string | null;
}

export const ReferenceBlock = memo(function ReferenceBlock({
  part,
  subtitle,
  onOpen,
  active,
  previews,
  previewsHidden,
  activeRef,
}: ReferenceBlockProps) {
  const card = referenceCard(part);
  if (!card) return null;
  const Icon = REFERENCE_ICON[card.target];

  const opener = (
    /*
     * A BUTTON, not a card with a link in it. The whole row is the affordance, so the target is
     * large enough for touch and there is exactly one thing to tab to — §8.5's 24px floor and one
     * clear CTA rather than a card plus a competing "view" link.
     *
     * `aria-expanded` because that is what this control does: it discloses a surface elsewhere on
     * the page. `aria-pressed` would say it is a toggle button, which reads as a mode change.
     */
    /*
     * ── AN OPENER IS THE QUIETEST TIER, AND THAT IS THE WHOLE POINT ───────────
     *
     * Every block in a turn used to be the same bordered, filled, rounded card: the table opener,
     * the changes opener, the plan doc, "Checks outstanding" and "Deployment failed" all arrived
     * at identical weight, and only the last two carried colour. Five equal rectangles in one turn
     * is a dashboard — nothing is dominant, so everything has to be read to find the one that
     * matters. Seen in the deploy-failed screenshots at every width.
     *
     * The turn now has three tiers, and this is the bottom one:
     *
     *   ATTENTION  failures / outstanding checks — colour, border, fill  (`StatusReferenceBlock`)
     *   DECISION   a question or approval          — accent rule + fill  (`InteractionCard`)
     *   OPEN       "there is more of this elsewhere" — THIS: no border, no fill at rest
     *
     * Resting state is a plain row on the page background: an icon, a label, and a chevron that
     * only reveals its verb on hover or focus. It still reads as interactive (pointer, hover fill,
     * chevron) and is still ONE full-width button, so the touch target and the single tab stop are
     * unchanged. Active keeps the accent fill — that one is state, not decoration, and it is how
     * the reader knows which card the open workspace belongs to.
     *
     * THE CTA IS DIMMED, NOT REMOVED — and that distinction is the whole reason this works.
     * `opacity-0` leaves the text in the DOM and in the accessibility tree, so the button's
     * accessible name is still "Changes 1 component · 1 new Review changes", exactly as it was
     * before the visual change. (`display:none`, `visibility:hidden` or an `aria-label` override
     * would each have rewritten that name — the last one silently, which is how the first attempt
     * at this broke four sweep checks that locate cards by name.)
     */
    <button
      type="button"
      onClick={() => onOpen(card.target, card.ref)}
      aria-expanded={!!active}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border border-primary/45 bg-primary/5"
          : "border border-transparent hover:border-border/60 hover:bg-muted/40",
      )}
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-foreground">{card.title}</span>
        {/* Derived from live state, never from the id — see `referenceCard`. */}
        {subtitle && <span className="block truncate text-[12px] text-muted-foreground">{subtitle}</span>}
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-muted-foreground">
        {/*
          Revealed on hover AND on `group-focus-visible` — a keyboard reader never fires hover, so
          without the second the verb would be permanently invisible to them while still being read
          out as part of the button's name. The chevron stays visible at every state, so the row is
          legible as an opener even while the verb is dimmed.
        */}
        <span
          className={cn(
            "transition-opacity motion-reduce:transition-none",
            active ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
          )}
        >
          {active ? "Close" : card.cta}
        </span>
        <ChevronRight aria-hidden className="h-3.5 w-3.5" />
      </span>
    </button>
  );

  if (card.target !== "changeset" || !previews?.length) return opener;
  return (
    <ChangePreviewList
      opener={opener}
      previews={previews}
      hidden={previewsHidden ?? 0}
      cta={card.cta}
      onOpen={onOpen}
      active={!!active}
      activeRef={activeRef}
    />
  );
});

/**
 * The changes card with the code under it — shared by the part-derived and the state-derived card.
 *
 * ── THE CODE UNDER THE CARD ────────────────────────────────────────────────────────────────────
 *
 * "Changes · 1 component · 1 new" told the reader the SIZE of the change and made them open the
 * board to learn what it was. The thing a write run produces is code, and code is what a reader
 * judges — so the first lines of each file sit right here, under the card, and every one of them
 * is a doorway to that file's diff. The card stays the way into the whole board.
 */
function ChangePreviewList({
  opener,
  previews,
  hidden,
  cta,
  onOpen,
  active,
  activeRef,
}: {
  opener: React.ReactNode;
  previews: ChangePreview[];
  hidden: number;
  cta: string;
  onOpen: (section: OpenableSection, focusRef: string | null) => void;
  active: boolean;
  activeRef?: string | null;
}) {
  return (
    <div className="flex flex-col gap-2" data-weave-changes="">
      {opener}
      <ul aria-label="Changed files" className="flex flex-col gap-2">
        {previews.map((preview) => (
          // Component AND file: `file_id` is a hash of (component, path) and unique in real data,
          // but a fixture that hands one file to three components must not warn or drop a card.
          <li key={`${preview.componentId}:${preview.fileId}`} className="min-w-0">
            <ChangePreviewCard
              preview={preview}
              onOpen={onOpen}
              // The board is open ON this file — `focusRef` is what seeded its selection.
              active={active && (activeRef ?? null) === preview.fileId}
            />
          </li>
        ))}
        {hidden > 0 && (
          <li className="px-1 text-[12px] text-muted-foreground">
            +{hidden} more file{hidden === 1 ? "" : "s"} — {cta} for all of them
          </li>
        )}
      </ul>
    </div>
  );
}

// ── Error ───────────────────────────────────────────────────────────────────

export const ErrorBlock = memo(function ErrorBlock({ part }: { part: Part }) {
  /*
   * A part-scoped error, rendered where the part is.
   *
   * `routeErrors` sends `scope: "part"` errors to their part and deliberately keeps them out of the
   * run error rail, so this is the only place one appears. `presentError` supplies the sentence, so
   * the copy here cannot drift from the rail's.
   */
  const body = (part.error ?? part.body ?? {}) as Partial<ProtocolError>;
  if (!body.code && !body.message) return null;
  const shown = presentError(body as ProtocolError);
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-md border border-ma-err-line bg-ma-err-soft px-2.5 py-2 text-[13px] leading-snug"
    >
      <AlertTriangle aria-hidden className="mt-[3px] h-3.5 w-3.5 shrink-0 text-ma-err-text" />
      <span className="min-w-0">
        <span className="font-medium text-foreground">{shown.headline}</span>
        {shown.detail && <span className="ml-1 break-words text-muted-foreground">{shown.detail}</span>}
        {shown.hint && <span className="ml-1 text-muted-foreground">{shown.hint}</span>}
      </span>
    </p>
  );
});

// ── Derived reference card ──────────────────────────────────────────────────

export interface DerivedReferenceBlockProps {
  reference: DerivedReference;
  onOpen: (section: OpenableSection, focusRef: string | null) => void;
  active?: boolean;
  /** The workspace's `focusRef` — which previewed file, if any, the open board is on. */
  activeRef?: string | null;
}

/**
 * The same card as `ReferenceBlock`, for a subject derived from state rather than from a part.
 *
 * ONE VISUAL LANGUAGE, TWO SOURCES. A reader must not be able to tell whether a card came from an
 * emitted `Part` or from `deriveReferences` — the distinction is a producer implementation detail.
 * The only difference is `tone`: a derived card knows the subject's state (a failed deploy), where a
 * part's `meta.title` is a static label, so it can carry the emphasis a failure needs.
 */
export const DerivedReferenceBlock = memo(function DerivedReferenceBlock({
  reference,
  onOpen,
  active,
  activeRef,
}: DerivedReferenceBlockProps) {
  const Icon = REFERENCE_ICON[reference.target];
  const opener = (
    <button
      type="button"
      onClick={() => onOpen(reference.target, null)}
      aria-expanded={!!active}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border-primary/45 bg-primary/5"
          : reference.tone === "bad"
            ? "border-ma-err-line bg-ma-err/[0.04] hover:bg-ma-err/[0.07]"
            : reference.tone === "warn"
              ? "border-ma-warn-line bg-ma-warn/[0.05] hover:bg-ma-warn/[0.08]"
              /*
                A TONELESS STATUS ROW IS NOT AN ALARM, so it drops to the opener tier.
                `bad` and `warn` keep their colour, border and fill — they are the ATTENTION tier
                and, now that plain openers carry no border at all, they are the only bordered
                things in a healthy turn and read as such. A `tone: null` row ("Deployment
                succeeded") reports rather than warns, so a bordered card for it put a neutral
                fact at the same weight as a failure directly above it.
              */
              : "border-transparent hover:border-border/60 hover:bg-muted/40",
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          "h-4 w-4 shrink-0",
          reference.tone === "bad"
            ? "text-[var(--ma-state-err-text)]"
            : reference.tone === "warn"
              ? "text-[var(--ma-state-warn-text)]"
              : "text-muted-foreground",
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-foreground">{reference.title}</span>
        {reference.subtitle && (
          <span className="block truncate text-[12px] text-muted-foreground">{reference.subtitle}</span>
        )}
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-muted-foreground group-hover:text-foreground">
        {active ? "Close" : reference.cta}
        <ChevronRight aria-hidden className="h-3.5 w-3.5" />
      </span>
    </button>
  );

  const previews = reference.previews ?? [];
  if (!previews.length) return opener;
  return (
    <ChangePreviewList
      opener={opener}
      previews={previews}
      hidden={reference.previewsHidden ?? 0}
      cta={reference.cta}
      onOpen={onOpen}
      active={!!active}
      activeRef={activeRef}
    />
  );
});

// ── A changed file, previewed ───────────────────────────────────────────────

const PREVIEW_STATUS: Record<ChangePreview["status"], { word: string; className: string }> = {
  // Built from the semantic parts, never a palette colour — the same green/red every status wears.
  created: { word: "new", className: "border-ma-ok-line text-[var(--ma-state-ok-text)]" },
  modified: { word: "modified", className: "border-border text-muted-foreground" },
  deleted: { word: "deleted", className: "border-ma-err-line text-[var(--ma-state-err-text)]" },
};

export interface ChangePreviewCardProps {
  preview: ChangePreview;
  onOpen: (section: OpenableSection, focusRef: string | null) => void;
  /** The changes board is open on THIS file. */
  active?: boolean;
}

/**
 * The first lines of a file the run wrote, and the way into its diff.
 *
 * ── WHAT IT IS ──────────────────────────────────────────────────────────────────────────────────
 *
 * ONE button, top to bottom: the header names the file (its name, its folder, whether it is new or
 * changed or going, how long it is), the body shows the first `CHANGE_PREVIEW_LINES` lines with
 * the same highlighter the answer's own code blocks use, and the footer says how much more there
 * is. The whole card opens the changes board ON this file — `onOpen("changeset", fileId)`, which
 * `ChangesetPane` turns into a selected row — so the click lands on the diff, not on the board's
 * first file.
 *
 * ── WHAT IT DELIBERATELY IS NOT ─────────────────────────────────────────────────────────────────
 *
 *   - Not a scroll box. A preview inside a scrolling conversation must not scroll itself; long
 *     lines are TRUNCATED with an ellipsis, and the footer's line count is the honest "there is
 *     more". The diff is one click away and is the place to read the whole thing.
 *   - Not a copy/download surface. The board and the editor already have both; a third copy
 *     control on an excerpt would copy eight lines of a class and call it the class.
 *   - Not a second Monaco. The bytes are painted as spans, exactly as `ChatCodeBlock` paints them.
 *
 * The excerpt is `<span>`s rather than a `<pre>` because the card is a `<button>`, whose content
 * model is phrasing content: a `<pre>` inside it is invalid HTML that browsers repair by ejecting
 * the block, which is how a click on the code stopped opening anything.
 */
export const ChangePreviewCard = memo(function ChangePreviewCard({ preview, onOpen, active }: ChangePreviewCardProps) {
  const { isDark, themeKey } = useIsDarkPainted();
  const code = preview.lines.join("\n");
  const tokens = useShikiTokens(code, highlightLanguage(preview.language), isDark, themeKey);
  const more = preview.lineCount - preview.lines.length;
  const status = PREVIEW_STATUS[preview.status];
  // The folder, for the muted part of the header. Split off the name rather than re-parsed: the
  // path is the backend's and is never rebuilt here.
  const dir = preview.path.endsWith(`/${preview.name}`)
    ? preview.path.slice(0, -(preview.name.length + 1))
    : preview.path === preview.name
      ? ""
      : preview.path;
  const lines = `${preview.lineCount} line${preview.lineCount === 1 ? "" : "s"}`;

  return (
    <button
      type="button"
      onClick={() => onOpen("changeset", preview.fileId)}
      aria-expanded={!!active}
      // Names the file AND what happened to it, so the button reads as more than "Open".
      aria-label={`Open the diff for ${preview.name} — ${status.word}, ${lines}`}
      data-weave-change-preview={preview.fileId}
      className={cn(
        "group flex w-full min-w-0 flex-col overflow-hidden rounded-lg border bg-card text-left transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "border-primary/45" : "border-border/60 hover:border-primary/40",
      )}
    >
      <span className="flex w-full min-w-0 items-center gap-2 bg-muted/40 px-3 py-1.5">
        <FileCode2 aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-mono text-[12px] font-medium text-foreground">{preview.name}</span>
          {dir && <span className="ml-1.5 text-[11px] text-muted-foreground">{dir}</span>}
        </span>
        <span className={cn("shrink-0 rounded-full border px-1.5 py-px text-[11px] font-medium", status.className)}>
          {status.word}
        </span>
        <span className="hidden shrink-0 text-[11px] tabular-nums text-muted-foreground sm:inline">
          {preview.language ? `${preview.language} · ` : ""}
          {lines}
        </span>
        <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </span>
      <span className="block w-full min-w-0 px-3 py-2 font-mono text-[13px] leading-5 text-foreground">
        <HighlightedLines code={code} tokens={tokens} lineClassName="block truncate" />
      </span>
      {more > 0 && (
        <span className="flex w-full items-center justify-between gap-2 border-t border-border/60 px-3 py-1.5 text-[12px] text-muted-foreground">
          <span>
            +{more} more line{more === 1 ? "" : "s"}
          </span>
          {/* The verb is dimmed, not removed — see `ReferenceBlock` on why `opacity-0` and not `display:none`. */}
          <span
            className={cn(
              "font-medium text-[var(--ma-accent-text)] transition-opacity motion-reduce:transition-none",
              active ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
            )}
          >
            {active ? "Close" : "Open the diff"}
          </span>
        </span>
      )}
    </button>
  );
});

// ── The first ten seconds ───────────────────────────────────────────────────

/**
 * Weave's own warm-up lines, verbatim from `WeaveShimmerCaption`.
 *
 * PORTED, NOT REUSED. That component reads `multiAgentStore` — nodes and reasoning buffers the v1
 * path never fills — so mounting it here would have rendered a permanent first line. The COPY is the
 * product's voice and already shipped, so it is kept exactly; only the trigger changes, from legacy
 * node telemetry to the v1 turn phase.
 */
const WEAVE_LINES = [
  "Let's weave…",
  "Stitching the threads…",
  "Threading the needle…",
  "Finding the thread…",
  "Warping the loom…",
  "Picking up the pattern…",
  "Tying off loose ends…",
];

function useRotatingLine(active: boolean): string {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return;
    // 2.2s, the same cadence legacy shipped: slow enough to read, fast enough to prove it is alive.
    const t = setInterval(() => setI((n) => (n + 1) % WEAVE_LINES.length), 2200);
    return () => clearInterval(t);
  }, [active]);
  return WEAVE_LINES[i % WEAVE_LINES.length];
}

/**
 * The answer's shape, reserved, while nothing has arrived yet.
 *
 * WHY A SKELETON AND NOT JUST A LINE. Measured in a browser: from Send to the first assistant part
 * was ten seconds, and the conversation was an empty black column for all of it. A line saying
 * "Working…" fixes the "is it alive" question and not the "where will the answer be" one — the page
 * still jumps when content lands. This is legacy's `PreSynthSkeleton` shape (accent pulse strip plus
 * two shimmer rows) with the warm-up line blended in as the top row, on the same `ma-pulse` /
 * `ma-skeleton` / `ma-weave-shimmer` animations the app already ships. No new keyframes.
 */
export function AnswerSkeleton({ announce = true }: { /** `false` inside a host that already owns the status region (the turn story). */ announce?: boolean } = {}) {
  const line = useRotatingLine(true);
  return (
    <div className="ma-fade-up flex items-start gap-3" {...(announce ? { "aria-live": "polite" as const, role: "status" } : {})}>
      {/*
        `ma-skeleton-vivid-bar` — was a flat `--ma-accent` fill. A single accent bar next to two flat
        grey rows read as barely alive; a three-stop gradient riding the same `ma-pulse` opacity
        animation says "in progress" at a glance, requested directly ("gradiant colorfull skeletion").
      */}
      <span
        aria-hidden
        className="ma-skeleton-vivid-bar ma-skeleton-vivid-bar--pulse"
        style={{
          width: 3,
          alignSelf: "stretch",
          minHeight: 64,
          borderRadius: 2,
          animation: "ma-pulse 1.6s var(--ma-ease) infinite",
          flex: "none",
        }}
      />
      <div className="flex flex-1 flex-col gap-3">
        <div
          className="ma-weave-shimmer"
          style={{ fontSize: 13, fontWeight: 500, letterSpacing: "0.01em", lineHeight: "18px" }}
        >
          {line}
        </div>
        <div className="ma-skeleton-vivid h-3 w-5/6" />
        <div className="ma-skeleton-vivid h-3 w-1/2" />
      </div>
    </div>
  );
}

/**
 * "It is doing this" — the working line, once there is something real to name.
 *
 * `label` is a tool's own `display_name` from the protocol (`workingActivity`). With no label it
 * says only that it is working, which is the honest floor: nothing in the 31 events describes
 * routing or planning before the first task, so a stage name here would be invented.
 */
/**
 * THE LIVE EDGE, at the bottom of the growing response.
 *
 * One line that always says where the stream is right now — thinking, running a named tool,
 * writing, or between hops — and always sits LAST, under everything the run has produced so far.
 * The parts above it carry their own live treatment (the reasoning rail, the prose cursor, the
 * activity spinner), but a reader who has scrolled to the end of the plan block or the plan.md
 * card needs the answer to "is it still going?" exactly there, not above the plan where the first
 * indicator happened to render. `role="status"` so the change of label is announced.
 */
export function LiveEdge({ label, kind }: { label: string; kind: "reasoning" | "tool" | "text" | "idle" }) {
  return (
    <div role="status" aria-live="polite" data-weave-live-edge={kind} className="ma-fade-up flex items-center gap-2.5 pt-1">
      <span className="ma-dot ma-dot--busy" style={{ width: 7, height: 7, flex: "none" }} />
      <span className={cn("text-[13px]", kind === "reasoning" ? "ma-weave-shimmer font-medium" : "text-muted-foreground")}>
        {label}
      </span>
    </div>
  );
}

export function WorkingLine({ label }: { label?: string | null }) {
  return (
    <div role="status" aria-live="polite" className="ma-fade-up flex items-center gap-2.5">
      <span className="ma-dot ma-dot--busy" style={{ width: 7, height: 7, flex: "none" }} />
      <span className="text-[13px] text-muted-foreground">{label ?? "Working…"}</span>
      <span className="ma-skeleton-vivid" style={{ height: 7, flex: 1, maxWidth: 160 }} />
    </div>
  );
}

/**
 * The live edge. Legacy's `StreamingCursor`, unchanged — a 2px accent bar on `ma-pulse`.
 *
 * It is the difference between "the answer is still arriving" and "the answer stops here", and
 * without it a paused stream and a finished one look identical.
 */
export function StreamCursor() {
  return (
    <span
      aria-hidden
      className="inline-block align-baseline"
      style={{
        width: 2,
        height: 14,
        marginLeft: 2,
        background: "var(--ma-accent)",
        animation: "ma-pulse 1s steps(2) infinite",
        verticalAlign: "text-bottom",
      }}
    />
  );
}

/**
 * What the answer is standing on.
 *
 * One quiet row of chips under the message, not a panel and not a badge per claim. A chip that names
 * a component opens the changeset board at it — the same click the file rows use — and anything the
 * producer marked `verified: false` wears it, because §2.20 makes an unverified claim visible or it
 * is not honest.
 */
export function SourcesRow({
  sources,
  onOpenComponent,
}: {
  sources: SourceChip[];
  onOpenComponent?: (componentId: string) => void;
}) {
  if (!sources.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
      <span className="text-[12px] text-muted-foreground">Based on</span>
      {sources.map((source) => {
        const openable = !!source.componentId && !!onOpenComponent;
        const chip = (
          <>
            <span className="truncate">{source.label}</span>
            {!source.verified && (
              <span
                className="shrink-0 rounded bg-ma-warn-soft px-1 text-[9.5px] font-semibold uppercase tracking-wide text-[var(--ma-state-warn-text)]"
                title={source.unverifiedReason ?? "Not verified against tool evidence"}
              >
                unverified
              </span>
            )}
          </>
        );
        return openable ? (
          <button
            key={source.citationId}
            type="button"
            onClick={() => onOpenComponent?.(source.componentId as string)}
            title={`Open ${source.label}`}
            className="inline-flex max-w-[240px] items-center gap-1.5 rounded-full border border-border/50 px-2 py-0.5 text-[12px] text-muted-foreground transition-colors motion-reduce:transition-none hover:border-border hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {chip}
          </button>
        ) : (
          <span
            key={source.citationId}
            className="inline-flex max-w-[240px] items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[12px] text-muted-foreground"
          >
            {chip}
          </span>
        );
      })}
    </div>
  );
}

// ── The end of an answer ────────────────────────────────────────────────────

/**
 * How a completed turn ENDS.
 *
 * WHAT WAS WRONG. A finished v1 answer ended with the word "Done." and an empty composer. Legacy
 * ends every terminal answer with `MessageFeedback` — thumbs up fires immediately, thumbs down opens
 * a reason picker — and that was a regression, not a simplification: the one moment a user is most
 * able to tell us whether the answer was any good, and v1 asked nothing.
 *
 * REUSED, NOT REBUILT. `MessageFeedback` needs a `runId` and nothing else: it calls
 * `sendFeedback(runId, rating, comment, reasons)` → `POST /api/runs/{id}/feedback`, and its
 * `useBadResponseFeedback` hook imports no store. So the legacy component mounts here verbatim.
 * There is no v1 feedback system, no second endpoint, and no duplicated state.
 *
 * THE NEXT ACTION IS DELIBERATELY MODEST. Run actions live in the action bar, which reads
 * `state.actions` — putting an Approve or a Deploy here too would be the duplicate-control mistake
 * the Stop/Cancel pair already made. What a reader wants at the end of an ANSWER is to ask the next
 * thing, so that is what this offers, and it just focuses the composer.
 */
export function TurnCompletion({
  runId,
  closing,
  followUps,
  onAsk,
  onExport,
  exporting,
  answerMarkdown,
  usageLabel,
  noSourcesLabel,
}: {
  runId: string | null;
  closing?: string | null;
  /** Suggested next questions, from legacy's own catalogue — see `followUps` in `WeaveTranscript`. */
  followUps?: { label: string; hint: string; question: string }[];
  /** Puts a question in the composer. NOT a send: the reader still decides. */
  onAsk?: (question: string) => void;
  onExport?: () => void;
  exporting?: boolean;
  /**
   * The answer's own markdown, for Copy.
   *
   * Passed in rather than read from a store: this component renders inside a turn and the turn
   * already holds its parts, while a store read would give it the LIVE run's answer on every past
   * turn in the conversation — the wrong text on all but one of them.
   */
  answerMarkdown?: string;
  /** The turn's spend ("$0.03"), shown in the action row beside Copy/Export instead of on a line of its own. */
  usageLabel?: string | null;
  /** A muted tag in the row for an answer that read nothing from the org ("No org sources read"). */
  noSourcesLabel?: string | null;
}) {
  /*
   * Legacy's own hook, unmodified — same 1.2s "Copied" window, same clipboard call, same cleanup on
   * unmount. Copy is the one answer action the v1 footer was missing next to feedback and export,
   * and rebuilding it would have been a second implementation of four lines.
   */
  const { copied, copy } = useCopyToClipboard();
  return (
    <div className="ma-fade-up flex flex-col gap-3">
      {closing && <p className="text-[14px] text-muted-foreground">{closing}</p>}

      {/*
        NEXT QUESTIONS, and they FILL THE COMPOSER rather than firing a run.
        
        Legacy's chips call `ask()` and start work on one click. Putting the question in the composer
        instead keeps the decision with the reader — they can edit it, or not send it — which is the
        user-driven half of this redesign. One click either way; only the commitment differs.
      */}
      {!!followUps?.length && onAsk && (
        <div className="flex flex-wrap gap-1.5">
          {followUps.map((suggestion) => (
            <button
              key={suggestion.question}
              type="button"
              onClick={() => onAsk(suggestion.question)}
              title={suggestion.hint}
              className="inline-flex max-w-[280px] items-center gap-1.5 rounded-full border border-border/50 px-2.5 py-1 text-[13px] text-muted-foreground transition-colors motion-reduce:transition-none hover:border-border hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="truncate">{suggestion.label}</span>
              <ChevronRight aria-hidden className="h-3 w-3 shrink-0" />
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* Legacy's own component, unmodified: same thumbs, same reason picker, same endpoint.
            Only with a run to post to: feedback is run-keyed, and a session-keyed turn (lane v2)
            has no endpoint — thumbs that silently do nothing are a fake affordance (§1). */}
        {runId && <MessageFeedback runId={runId} />}
        <div className="flex flex-wrap items-center gap-2">
        {noSourcesLabel && (
          <span data-testid="no-sources-tag" className="text-[13px] text-muted-foreground">
            {noSourcesLabel}
          </span>
        )}
        {answerMarkdown?.trim() && (
          <button
            type="button"
            // An evidence line is `[kind](artifact://<id>)`; the id means nothing outside the app, so the copy carries the kind only.
            onClick={() => void copy(exportArtifactMarkdown(answerMarkdown))}
            /*
             * The label changes, so the accessible name changes with it — a button that says "Copy"
             * while reading "Copied" to a screen reader is two different controls to two readers.
             */
            aria-label={copied ? "Answer copied" : "Copy answer"}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {copied ? (
              <Check aria-hidden className="h-3.5 w-3.5" />
            ) : (
              <Copy aria-hidden className="h-3.5 w-3.5" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
        {onExport && (
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-muted/40 hover:text-foreground disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {exporting ? (
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
            ) : (
              <Download aria-hidden className="h-3.5 w-3.5" />
            )}
            Export
          </button>
        )}
        {usageLabel && (
          <span data-testid="turn-usage" className="text-[13px] tabular-nums text-muted-foreground">
            {usageLabel}
          </span>
        )}
        </div>
      </div>
    </div>
  );
}

// ── The plan, in the conversation ───────────────────────────────────────────// ── The plan, in the conversation ───────────────────────────────────────────

export interface PlanSummaryBlockProps {
  summary: PlanSummary;
  onReview: () => void;
  active?: boolean;
  /**
   * Opens `summary.docRef` — the plan as a document — in the workspace. Omitted when the plan has
   * no document yet, or the host has nowhere to open one; the link is then simply not offered.
   */
  onOpenDoc?: () => void;
  /** The Document section is open on this plan's document. */
  docActive?: boolean;
}

/** Component chips per step before "+N". Two keeps a row to one line at the chat's width. */
const STEP_CHIPS = 2;

/**
 * The word beside a chip for an op worth calling out. `modify` is the unmarked case — most steps
 * change something — so it carries no word; the rest say what kind of change it is.
 */
const OP_WORD: Record<string, string> = {
  create: "new",
  delete: "delete",
  activate: "activate",
  assign: "assign",
  analyze: "analyze",
};

/**
 * What a step touches, as chips under its title — `ComponentRef` names, nothing invented.
 *
 * WHY CHIPS AND NOT A SENTENCE. "Approval routing service" is the step; `OpportunityApprovalService`
 * is the component it writes. The reader scanning the plan wants both at a glance, and a mono chip
 * is how the rest of this surface already names a component (the changeset board, the sources
 * row). Capped, because a step that touches eleven fields is a story for the plan pane.
 */
function StepComponents({ components, settled }: { components: PlanSummaryComponent[]; settled?: boolean }) {
  if (!components.length) return null;
  const shown = components.slice(0, STEP_CHIPS);
  const more = components.length - shown.length;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1">
      {shown.map((component, index) => {
        const op = component.op ? OP_WORD[component.op] : null;
        return (
          <span
            key={`${index}:${component.name}`}
            data-step-component={component.name}
            title={[component.type, component.op].filter(Boolean).join(" · ")}
            className={cn(
              "inline-flex max-w-full items-center gap-1 rounded border border-border/60 bg-[var(--ma-surface-soft)] px-1.5 py-px font-mono text-[11px] leading-4",
              // A finished step recedes with its title: the chip stays legible, just not loud.
              settled ? "text-muted-foreground" : "text-foreground",
            )}
          >
            <span className="truncate">{component.name}</span>
            {op && <span className="shrink-0 font-sans text-muted-foreground">{op}</span>}
          </span>
        );
      })}
      {more > 0 && <span className="text-[11px] text-muted-foreground">+{more}</span>}
    </span>
  );
}

/**
 * A step's title, what it touches and — at the step that is live, waiting or broke — why.
 *
 * `rationale` only where it helps: it is the backend's "why this mechanism", and at a step that is
 * quietly done it is history, while at the one being worked it is the reader's answer to "why is
 * it writing Apex for this".
 */
function StepBody({
  row,
  emphasised,
  settled,
  title,
}: {
  row: PlanSummaryRow;
  emphasised: boolean;
  /** The step is done — its chips fade with its title. */
  settled?: boolean;
  title: React.ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <span>{title}</span>
      <StepComponents components={row.components} settled={settled} />
      {emphasised && row.rationale && (
        <span className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{row.rationale}</span>
      )}
    </span>
  );
}

/**
 * "I can make this change in 3 steps" — the whole plan the chat shows.
 *
 * WHAT THIS REPLACED. A reference card reading "Plan · 2 steps · mixed", which told the reader the
 * plan's size and made them open a panel to learn its content. The shape of the work is the one
 * thing worth reading in the flow of a conversation; every step's lane, phase, op, mechanism,
 * component list and id is what the workspace is for.
 *
 * NOT A CARD. An ordered list and one button — §13's "everything being a card" is what a bordered
 * box around the plan summary, inside a bordered conversation, beside a bordered workspace, adds up
 * to. The numbers carry the structure.
 */
type PlanOutcome = PlanSummary["outcome"];

/** How a step reads, given its own status AND what happened to the run around it. */
type StepView = "done" | "running" | "pending" | "waiting" | "failed" | "skipped" | "stopped" | "unreported";

function stepView(status: StepStatus, outcome: PlanOutcome): StepView {
  switch (status) {
    case "done":
      return "done";
    case "failed":
    case "blocked":
      return "failed";
    case "skipped":
    case "deferred":
      return "skipped";
    case "waiting_input":
      return "waiting";
    case "running":
      // The backend never closes a step when the run ends around it; the run's status says.
      if (outcome === "stopped") return "stopped";
      if (outcome === "failed") return "failed";
      if (outcome === "finished" || outcome === "partial") return "unreported";
      if (outcome === "waiting") return "waiting";
      return "running";
    default:
      // pending
      if (outcome === "finished" || outcome === "partial") return "unreported";
      if (outcome === "stopped" || outcome === "failed") return "stopped";
      return "pending";
  }
}

const STEP_VIEW_LABEL: Record<StepView, string> = {
  done: "done",
  running: "in progress",
  pending: "not started",
  waiting: "waiting for your answer",
  failed: "failed",
  skipped: "skipped",
  stopped: "not started — the run stopped",
  unreported: "not reported as done",
};

/**
 * One glyph per step view — the tick the reader asked for, and its siblings.
 *
 * Every state has a SHAPE, not just a colour (§16): a filled tick, a spinner, a hollow circle, a
 * question mark, a warning triangle, a dash. The text label beside it is read to assistive tech via
 * `sr-only` in the row, so the glyph is decoration over data that is also said.
 */
function StepGlyph({ view }: { view: StepView }) {
  switch (view) {
    case "done":
      return <Check aria-hidden className="ma-plan-check h-4 w-4 text-[var(--ma-state-ok-text)]" strokeWidth={2.5} />;
    case "running":
      return <Loader2 aria-hidden className="h-4 w-4 animate-spin text-[var(--ma-accent-text)] motion-reduce:animate-none" />;
    case "waiting":
      return <HelpCircle aria-hidden className="h-4 w-4 text-[var(--ma-state-warn-text)]" />;
    case "failed":
      return <AlertTriangle aria-hidden className="h-4 w-4 text-[var(--ma-state-err-text)]" />;
    case "skipped":
    case "stopped":
    case "unreported":
      return <MinusCircle aria-hidden className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Circle aria-hidden className="h-4 w-4 text-muted-foreground" />;
  }
}

/** The status pill in the plan block's header — one word, and the run's word before the steps'. */
function PlanStatusPill({ outcome, progress }: { outcome: PlanOutcome; progress: PlanSummary["progress"] }) {
  const base = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums";
  switch (outcome) {
    case "proposal":
      return <span className={cn(base, "border-[hsl(var(--rs-warm,var(--primary))/0.4)] text-[var(--ma-accent-text)]")}>Proposed</span>;
    case "running":
      return (
        <span className={cn(base, "border-[hsl(var(--rs-warm,var(--primary))/0.4)] text-[var(--ma-accent-text)]")}>
          <Loader2 aria-hidden className="h-3 w-3 animate-spin motion-reduce:animate-none" />
          Executing{progress ? ` · ${progress.done} of ${progress.total}` : ""}
        </span>
      );
    case "waiting":
      return <span className={cn(base, "border-ma-warn-line text-[var(--ma-state-warn-text)]")}>Waiting on you</span>;
    case "finished":
      return <span className={cn(base, "border-ma-ok-line text-[var(--ma-state-ok-text)]")}>Done</span>;
    case "partial":
      return <span className={cn(base, "border-ma-warn-line text-[var(--ma-state-warn-text)]")}>Partly reported</span>;
    case "failed":
      return <span className={cn(base, "border-ma-err-line text-[var(--ma-state-err-text)]")}>Failed</span>;
    default:
      return <span className={cn(base, "border-border text-muted-foreground")}>Stopped</span>;
  }
}

/**
 * "I can make this change in 3 steps" — the whole plan the chat shows.
 *
 * ── THE SHAPE, AND WHY ────────────────────────────────────────────────────────────────────────
 *
 * One block, three states, read top-down: a HEADER row (the word "Plan", a status pill that says
 * what is true right now, and the way into the full plan), a one-sentence headline, and the steps.
 *
 *   proposal   a numbered list — nothing has happened, so no glyphs, and §3.1's promise under it.
 *   live       a checklist read straight off `Plan.steps[].status`: tick = done, spinner = in
 *              progress, hollow = to come, with a thin bar for done-over-total. The window keeps
 *              the live step in view on a long plan.
 *   ended      the same checklist, but the RUN's terminal status decides the reading — a step the
 *              backend never closed shows as "not reported" / "stopped", never as still working.
 *              When every step is done the block folds to its header, the way a finished checklist
 *              gets out of the way; a partial, failed or stopped plan stays open, because that is
 *              the thing the reader needs to see.
 *
 * NOT A CARD. A bordered box around the plan, inside a bordered conversation, beside a bordered
 * workspace, is the "everything is a card" §13 warns about. The header row and the glyph column
 * carry the structure.
 */
export const PlanSummaryBlock = memo(function PlanSummaryBlock({
  summary,
  onReview,
  active,
  onOpenDoc,
  docActive,
}: PlanSummaryBlockProps) {
  const { progress, outcome } = summary;
  const plural = (n: number) => (n === 1 ? "" : "s");
  const folded = outcome === "finished";
  /*
   * ── THE WHOLE PLAN, ON REQUEST ─────────────────────────────────────────────────────────────────
   *
   * `steps` is a window: five of a proposal, six around the live edge of a running plan. "+4 more
   * steps" used to be a sentence with no door — the only way to read step nine was the plan pane.
   * The rows are all here (`summary.rows`), so the sentence is now a toggle and the block grows in
   * place. Local state, because which reader wants the long form is not run state.
   */
  const [expanded, setExpanded] = useState(false);
  const total = summary.rows.length;
  const windowRows = summary.rows.slice(summary.hiddenBefore, summary.hiddenBefore + summary.steps.length);
  const canExpand = total > windowRows.length;
  const rows = expanded ? summary.rows : windowRows;
  const numberOffset = expanded ? 0 : summary.hiddenBefore;

  const showAll = canExpand && (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      className="inline-flex min-h-6 items-center rounded px-1 text-[13px] font-medium text-[var(--ma-accent-text)] hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {expanded ? "Show fewer" : `Show all ${total} steps`}
    </button>
  );

  const reviewButton = (
    <button
      type="button"
      onClick={(event) => {
        // Inside a `<summary>` when folded — the click must open the plan, not toggle the fold.
        event.stopPropagation();
        event.preventDefault();
        onReview();
      }}
      aria-expanded={!!active}
      // `min-h-7`: the 24px touch floor (§8.5) with room to spare — a 12px label alone measured 23.5px.
      className="inline-flex min-h-7 items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] font-medium text-[var(--ma-accent-text)] transition-colors motion-reduce:transition-none hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {active ? "Close plan" : "Review plan"}
      <ChevronRight aria-hidden className="h-3.5 w-3.5" />
    </button>
  );

  /*
   * THE DOCUMENT IS A VIEW OF THE PLAN, SO ITS DOOR IS ON THE PLAN. `plan.md` used to be a separate
   * "Document" row under this block — the same plan a second time, one line apart, and a reader
   * asked which one they were meant to open. The block IS the plan in the conversation; "Review
   * plan" is the detailed pane; "plan.md" is the persistent document. One subject, two exits, both
   * on its header. (After a reload the plan block is gone and the document row is what survives —
   * that is the archive's job, and the row still opens the same section.)
   */
  const docButton = onOpenDoc && summary.docRef && (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onOpenDoc();
      }}
      aria-expanded={!!docActive}
      aria-label={docActive ? "Close plan.md" : "Open plan.md"}
      title={docActive ? "Close plan.md" : "Open plan.md"}
      className={cn(
        "inline-flex min-h-7 items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[12px] transition-colors motion-reduce:transition-none hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        docActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <FileText aria-hidden className="h-3.5 w-3.5" />
      plan.md
    </button>
  );

  const header = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Sparkles aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="ma-micro text-muted-foreground">Plan</span>
      <PlanStatusPill outcome={outcome} progress={progress} />
      <span className="ml-auto inline-flex items-center gap-1">
        {docButton}
        {reviewButton}
      </span>
    </div>
  );

  const headline = <p className="text-[14.5px] leading-snug text-foreground">{summary.headline}</p>;

  const list = outcome === "proposal" ? (
    /*
      A PROPOSAL reads as a plain numbered list — no glyphs, because nothing has happened yet and
      a column of empty circles would say "not started" five times over. `list-decimal` so a
      screen reader announces "list, 3 items" and each position.
    */
    <ol className="ml-4 list-decimal space-y-1.5 text-[14px] leading-snug text-foreground marker:text-muted-foreground">
      {rows.map((row, index) => (
        <li key={`${numberOffset + index}-${row.title}`} className="pl-1">
          <StepBody row={row} emphasised={false} title={row.title} />
        </li>
      ))}
    </ol>
  ) : (
    /*
      THE PLAN AS IT HAPPENS: a tick on every finished step, a spinner on the one in progress,
      hollow circles for what is still to come — read straight off `Plan.steps[].status` and the
      run's own status. Rows keep their plan numbers even when the window has moved past the first
      few, so "step 7" here is step 7 in the plan pane.
    */
    <ol aria-label="Plan progress" className="space-y-1.5 text-[14px] leading-snug">
      {!expanded && summary.hiddenBefore > 0 && (
        <li className="pl-6 text-[13px] text-muted-foreground">
          {summary.hiddenBefore} earlier step{plural(summary.hiddenBefore)} done
        </li>
      )}
      {rows.map((row, index) => {
        const status = row.status;
        const view = stepView(status, outcome);
        const number = numberOffset + index + 1;
        const emphasised = view === "running" || view === "waiting" || view === "failed";
        return (
          <li
            key={`${number}-${row.title}`}
            data-step-status={status}
            data-step-view={view}
            className={cn(
              "flex items-start gap-2 transition-colors motion-reduce:transition-none",
              emphasised ? "text-foreground" : view === "done" ? "text-muted-foreground" : "text-foreground",
            )}
          >
            <span className="mt-[2px] w-4 shrink-0">
              <StepGlyph view={view} />
            </span>
            <span className="w-5 shrink-0 text-[13px] tabular-nums text-muted-foreground">{number}.</span>
            <StepBody
              row={row}
              emphasised={emphasised}
              settled={view === "done"}
              title={
                <span className={cn(view === "running" && "font-medium")}>
                  {row.title}
                  <span className="sr-only">
                    {" "}
                    — {STEP_VIEW_LABEL[view]}
                    {view === "running" && summary.currentInferred ? " (assumed: first unfinished step)" : ""}
                  </span>
                </span>
              }
            />
          </li>
        );
      })}
    </ol>
  );

  const tail = (
    <>
      {(canExpand || (!expanded && summary.hidden > 0)) && (
        <p className={cn("flex flex-wrap items-center gap-x-2 text-[13px] text-muted-foreground", outcome !== "proposal" && "pl-6")}>
          {!expanded && summary.hidden > 0 && (
            <span>
              +{summary.hidden} more step{plural(summary.hidden)}
            </span>
          )}
          {showAll}
        </p>
      )}
      {summary.promise && <p className="text-[13px] text-muted-foreground">{summary.promise}</p>}
    </>
  );

  const bar = progress && progress.total > 0 && (outcome === "running" || outcome === "waiting" || outcome === "partial") && (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={progress.total}
      aria-valuenow={progress.done}
      aria-label={`${progress.done} of ${progress.total} steps done`}
      className="h-1 w-full max-w-[28rem] overflow-hidden rounded-full bg-border"
    >
      <div
        className="ma-plan-bar h-full rounded-full"
        style={{ width: `${Math.round((progress.done / progress.total) * 100)}%`, background: "var(--ma-state-ok)" }}
      />
    </div>
  );

  if (folded) {
    /*
      A FINISHED PLAN GETS OUT OF THE WAY. Every step is done; the list is the record, not the
      news. It folds to its header + headline, and one click reopens it — a `<details>` so that
      works without any state of ours and is announced as a disclosure.
    */
    return (
      <details className="group flex flex-col gap-2" data-plan-folded="">
        <summary className="flex cursor-pointer list-none flex-col gap-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {header}
          <span className="flex items-center gap-1.5 text-[14.5px] leading-snug text-foreground">
            <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90 motion-reduce:transition-none" />
            {summary.headline}
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2 pl-5">
          {list}
          {tail}
        </div>
      </details>
    );
  }

  return (
    <section aria-label="Plan" className="flex flex-col gap-2">
      {header}
      {headline}
      {bar}
      {list}
      {tail}
    </section>
  );
});

// ── Outputs ─────────────────────────────────────────────────────────────────

export interface OutputsBlockProps {
  outputs: RunOutput[];
  onOpen: (target: OpenableSection, focusRef: string | null) => void;
  activeRef?: string | null;
}

/**
 * The things Weave produced, as results rather than as a file listing.
 *
 * QUIET WHEN THERE IS NOTHING. `runOutputs` returns `[]` for most runs — a pure-config change
 * generates no document — and an empty "Outputs" shelf is the dashboard-that-exists-because-the-
 * section-exists that §5 rules out.
 *
 * One row per output, each an opener. No size, no kind chip, no id: the contract does not carry a
 * size for an artifact reference, and inventing one would be fabrication.
 */
const OUTPUTS_LABEL_ID = "weave-outputs-label";

export const OutputsBlock = memo(function OutputsBlock({
  outputs,
  onOpen,
  activeRef,
}: OutputsBlockProps) {
  if (!outputs.length) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Names the list for assistive tech, so it is not an anonymous group of links. */}
      {/*
        THE PRODUCT DOES NOT NARRATE ITSELF IN THE THIRD PERSON.

        This read "Weave produced" / "Weave produced 3 things" — the assistant referring to itself
        by brand name, mid-conversation, to introduce its own output. Nobody says that; the reader
        already knows who produced it, because they are reading its answer. "things" is also the
        word you reach for when you have not decided what the list holds.

        "Documents" is what the list actually holds: `runOutputs` collects `artifact` parts plus the
        plan doc, and an untitled artifact already falls back to the word "Document" for its own row.
        The count is not repeated — the rows are right there to be counted.
      */}
      <p id={OUTPUTS_LABEL_ID} className="text-[12px] font-medium text-muted-foreground">
        {outputs.length === 1 ? "Document" : "Documents"}
      </p>
      <ul aria-labelledby={OUTPUTS_LABEL_ID} className="flex flex-col gap-1">
        {outputs.map((output) => {
          const active = activeRef === output.ref;
          return (
            <li key={output.ref}>
              <button
                type="button"
                onClick={() => onOpen(output.target, output.ref)}
                aria-expanded={active}
                className={cn(
                  // Opener tier (see `ReferenceBlock`): a file row points elsewhere, so it carries
                  // no border at rest and the group reads as a list rather than a stack of cards.
                  "group flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "border-primary/45 bg-primary/5"
                    : "border-transparent hover:border-border/60 hover:bg-muted/40",
                )}
              >
                <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-foreground">
                  {output.title}
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-muted-foreground group-hover:text-foreground">
                  {active ? "Close" : "Open"}
                  <ChevronRight aria-hidden className="h-3.5 w-3.5" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
});
